import { Link } from "react-router-dom";
import { LegalLayout, H2, H3, P, List, Note } from "./LegalLayout";
import { FOUND_CONFIG, contactTarget } from "./foundConfig";

export default function Terms() {
  const c = FOUND_CONFIG;
  const t = contactTarget();

  return (
    <LegalLayout
      title="FOUND Terms of Service"
      description="The terms governing use of FOUND, the music discovery app operated by Dream Big Entertainment Ltd."
    >
      <P>
        These Terms of Service (“Terms”) govern your use of <strong>FOUND</strong>, a music
        discovery social platform operated by <strong>{c.company}</strong> (“Dream Big”, “we”,
        “us”). By creating an account or using FOUND you agree to these Terms.
      </P>
      <P><strong>Effective date:</strong> {c.effectiveDate} · <strong>Last updated:</strong> {c.lastUpdated}</P>

      <H2>1. Eligibility</H2>
      <P>You must be old enough to use FOUND under the laws of your country (and at least 13). If you use FOUND on behalf of an organisation, you confirm you are authorised to accept these Terms for it.</P>

      <H2>2. Accounts</H2>
      <H3>Registration & security</H3>
      <P>You agree to provide accurate information, keep your credentials confidential and are responsible for activity on your account. Notify us of any unauthorised use.</P>
      <H3>Artist and Scout accounts</H3>
      <List items={[
        "Artist accounts may upload and release original music and video and access creator tools.",
        "Scout accounts discover Artists, build discovery history, and earn Scout Points and reputation.",
        "You are responsible for complying with these Terms regardless of account type.",
      ]} />

      <H2>3. User responsibilities & user-generated content</H2>
      <P>You are solely responsible for the content you upload or post, including music, video, artwork, comments and Scout Posts. You must have all rights necessary to share it.</P>

      <H2>4. Music and video uploads; ownership and licences</H2>
      <List items={[
        "You retain ownership of content you upload. FOUND does not claim ownership of your music, video or artwork.",
        "By uploading, you grant Dream Big a limited, worldwide, non-exclusive licence to host, store, reproduce, stream and display your content solely to operate, promote and improve FOUND.",
        "You represent and warrant that you own your content or have all necessary licences, permissions and rights (including master, publishing, composition and performance rights) to upload and distribute it on FOUND.",
        "You must not upload content that infringes anyone else's intellectual property or other rights.",
      ]} />
      <P>See our <Link to="/found/copyright" className="text-white underline">Copyright & IP Policy</Link> for details and reporting.</P>

      <H2>5. Comments and Scout Posts</H2>
      <P>Comments and Scout Posts must follow our <Link to="/found/community-guidelines" className="text-white underline">Community Guidelines</Link>. Keep contributions relevant to music discovery and respectful.</P>

      <H2>6. Prohibited content and conduct</H2>
      <List items={[
        "Copyright infringement, stolen music, or content you do not have rights to.",
        "Fake Artists, impersonation, or misrepresenting who you are.",
        "Harassment, hate, or abusive behaviour.",
        "Spam, fraud, bots, and automated or artificial engagement.",
        "Manipulation of rankings, Scout Points or reputation, including coordinated manipulation.",
        "Multiple-account abuse and Scout manipulation.",
        "Uploading content that is not music-first or that is otherwise unrelated to music discovery.",
        "Any illegal or seriously harmful activity.",
      ]} />

      <H2>7. Moderation, removal, suspension and termination</H2>
      <P>We may review, flag, remove or restrict content, and suspend or terminate accounts, that violate these Terms or our policies, or where necessary to protect the community or comply with law. You may report content and accounts through FOUND and the <Link to="/found/support" className="text-white underline">Support</Link> page.</P>

      <H2>8. Scout Points, levels, leaderboards and future rewards</H2>
      <List items={[
        "Scout Points are platform reputation and achievement metrics that reflect discovery activity.",
        "Scout Points currently have no cash value and are not a currency.",
        "Being eligible for any future reward programme does not guarantee payment, income or any specific reward.",
        "Dream Big may set additional eligibility, verification and fraud-prevention requirements for any future reward programmes, and may modify or discontinue points, levels and leaderboards.",
      ]} />

      <H2>9. Paid features (present and future)</H2>
      <P>FOUND is designed to support features such as FOUND Artist Pro, paid promotion, Gifts, Artist services (“Work With Me”) and advertising. Some of these may not be available yet. We do not represent that any subscription, payment, payout, gift or marketplace transaction is currently available unless it actually is. Additional terms will apply when such features launch.</P>
      <Note>
        <strong>Core principle:</strong> paid promotion may purchase additional exposure or testing.
        It does <strong>not</strong> guarantee organic ranking, Scout Points, GROWING/RISING/BREAKOUT
        status, popularity, followers or commercial success.
      </Note>

      <H2>10. Intellectual property (FOUND)</H2>
      <P>FOUND, the Dream Big and FOUND names, logos and software are owned by Dream Big and its licensors and are protected by law. These Terms do not grant you rights to our branding except as needed to use the Service.</P>

      <H2>11. Third-party links and services</H2>
      <P>FOUND may link to or rely on third-party services. We are not responsible for third-party content or practices; their terms and privacy policies apply.</P>

      <H2>12. Service availability & disclaimers</H2>
      <P>FOUND is provided “as is” and “as available”. We do not warrant that it will be uninterrupted, error-free or that any particular result (such as discovery, growth or success) will be achieved.</P>

      <H2>13. Limitation of liability</H2>
      <P>To the maximum extent permitted by law, Dream Big will not be liable for indirect, incidental, special, consequential or punitive damages, or for lost profits, data or goodwill, arising from your use of FOUND.</P>

      <H2>14. Changes to FOUND and to these Terms</H2>
      <P>We may change FOUND and update these Terms. Material changes will be reflected by updating the “Last updated” date and, where appropriate, additional notice. Continued use after changes means you accept the updated Terms.</P>

      <H2>15. Contact</H2>
      <P>
        Questions about these Terms? {t.isEmail
          ? <>Email <a href={t.href} className="text-white underline">{t.href.replace("mailto:", "")}</a>.</>
          : <>Reach us via <Link to="/found/support" className="text-white underline">FOUND Support</Link>.</>}
      </P>
    </LegalLayout>
  );
}
